import { defineMessages, type IntlShape } from 'react-intl';
import type {
  BlockConfigBase,
  BlocksFormData,
  JSONSchema,
  Brain,
} from '@plone/types';
import { Link } from '@plone/components';

import addBlocksFullWidth from '@redturtle/qubicaamf-theme/components/Blocks/extenders/addBlocksFullWidth';
import addBlocksBg from '@redturtle/qubicaamf-theme/components/Blocks/extenders/addBlocksBg';
import addBlockLines from '@redturtle/qubicaamf-theme/components/Blocks/extenders/addBlockLines';

export interface ProductsSliderSlide {
  '@id'?: string;
  link?: typeof Link;
  slide_content: Brain & { video_file: string; video_url: string };
  title?: string;
  title_color?: string;
  show_overlay?: boolean;
}

export interface ProductsSliderData extends JSONSchema, BlocksFormData {
  '@type': 'productsslider';
  slides: ProductsSliderSlide[];
  styles?: { fullwidth?: boolean };
  description?: string;
}

export const ProductsSliderSchema = ({
  data,
  intl,
}: {
  data: ProductsSliderData;
  intl: IntlShape;
}): JSONSchema => {
  const schema = {
    title: 'Products Slider',
    fieldsets: [
      {
        id: 'default',
        title: 'Default',
        fields: ['title', 'description', 'slides'],
      },
    ],
    properties: {
      title: {
        title: intl.formatMessage(messages.title_title),
      },
      description: {
        title: intl.formatMessage(messages.description_title),
        widget: 'textarea',
      },
      slides: {
        title: intl.formatMessage(messages.slides_title),
        widget: 'object_list',
        schema: {
          title: intl.formatMessage(messages.slide_title),
          fieldsets: [
            {
              id: 'default',
              title: 'Default',
              fields: [
                'title',
                'title_color',
                'slide_content',
                'link',
                'show_overlay',
              ],
            },
          ],
          properties: {
            title: { title: intl.formatMessage(messages.slide_title_title) },
            title_color: {
              title: intl.formatMessage(messages.slide_title_color_title),
              widget: 'color_picker',
              colors: [
                { name: 'white', label: 'White' },
                { name: 'black', label: 'Black' },
              ],
              default: 'black',
            },
            slide_content: {
              title: intl.formatMessage(messages.slide_content_title),
              widget: 'object_browser',
              return: 'single',
              mode: 'link',
              selectableTypes: ['Image', 'WildcardVideo'],
            },
            link: {
              title: intl.formatMessage(messages.slide_link_title),
              widget: 'object_browser',
              return: 'single',
              mode: 'link',
            },
            show_overlay: {
              title: intl.formatMessage(messages.showOverlay),
              type: 'boolean',
            },
          },
          required: ['slide_content'],
        },
      },
    },
    required: [],
  };

  addBlocksFullWidth({ schema, formData: data, intl });
  addBlocksBg({ schema, formData: data, intl });
  addBlockLines({ schema, formData: data, intl });

  return schema;
};

// export default ProductsSliderSchema;

export interface ProductsSliderConfig
  extends Omit<BlockConfigBase, 'blockSchema'> {
  blockSchema: typeof ProductsSliderSchema;
}

const messages = defineMessages({
  title_title: {
    id: 'Title',
    defaultMessage: 'Title',
  },
  description_title: {
    id: 'description_title',
    defaultMessage: 'Block description',
  },
  slides_title: {
    id: 'slides_title',
    defaultMessage: 'Slides',
  },
  slide_title: {
    id: 'slide_title',
    defaultMessage: 'Slide',
  },
  slide_title_title: {
    id: 'slide_title_title',
    defaultMessage: 'Slide title',
  },
  slide_title_color_title: {
    id: 'slide_title_color_title',
    defaultMessage: 'Title color',
  },
  slide_content_title: {
    id: 'slide_content_title',
    defaultMessage: 'Slide content',
  },
  slide_link_title: {
    id: 'slide_link_title',
    defaultMessage: 'Slide link',
  },
  showOverlay: { id: 'showOverlay', defaultMessage: 'Show overlay' },
});
